import { useEffect, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LineChartOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "../api";
import type { AgentSummary } from "../types";
import {
  consumePendingAgentActivation,
  consumePendingFeishuIdentitySync,
  hasRecentFeishuConnectorAuthorization,
  hasRecentFeishuOfficeAuthorization,
  hasRecentFeishuOfficeWriteAuthorization,
  hasRecentFeishuWriteAuthorization,
  hasRecentKnowledgeAuthorization
} from "../agent-activation";
import { consumePendingFeishuOfficeConnection } from "../feishu-office-authorization";

function AgentCard({
  agent,
  mine,
  onChanged
}: {
  agent: AgentSummary;
  mine: boolean;
  onChanged: () => void;
}) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const useAgent = async () => {
    try {
      const knowledge = agent.requiresKnowledgeAuthorization
        && !hasRecentKnowledgeAuthorization(sessionStorage);
      const connector = agent.requiresFeishuConnectorAuthorization
        && !hasRecentFeishuConnectorAuthorization(sessionStorage);
      const connectorWrite = agent.requiresFeishuWriteAuthorization
        && !hasRecentFeishuWriteAuthorization(sessionStorage);
      const office = agent.requiresFeishuOfficeAuthorization
        && !hasRecentFeishuOfficeAuthorization(sessionStorage);
      const officeWrite = agent.requiresFeishuOfficeWriteAuthorization
        && !hasRecentFeishuOfficeWriteAuthorization(sessionStorage);
      if (knowledge || connector || connectorWrite || office || officeWrite) {
        message.loading({ content: "正在确认飞书访问权限…", key: "activate-agent" });
        await api.authorizeFeishuAccess({ knowledge, connector, connectorWrite, office, officeWrite });
      }
      await api.activate(agent.id);
      message.success({
        content: `已切换到“${agent.name}”，现在可以返回飞书 Bot 对话`,
        key: "activate-agent"
      });
    } catch (error) {
      message.error({
        content: (error as Error).message || "Agent 启用失败，请重试",
        key: "activate-agent"
      });
    }
  };
  return (
    <Card className="agent-card" hoverable>
      <div className="agent-card-head">
        <span className="agent-icon" style={{ background: `${agent.themeColor}18`, color: agent.themeColor }}>
          {agent.icon}
        </span>
        <div className="agent-card-title">
          <Typography.Title level={5}>{agent.name}</Typography.Title>
          <Space size={4} wrap>
            <Tag color={agent.status === "published" ? "green" : "default"}>
              {agent.status === "published" ? "已发布" : "草稿"}
            </Tag>
            <Tag>{agent.visibility === "company" ? "全公司" : "仅自己"}</Tag>
          </Space>
        </div>
      </div>
      <Typography.Paragraph ellipsis={{ rows: 2 }} className="agent-description">
        {agent.description}
      </Typography.Paragraph>
      <div className="agent-meta">
        <span>{agent.creatorName || "我"}{agent.department ? ` · ${agent.department}` : ""}</span>
        <span>{agent.usageCount} 次使用</span>
      </div>
      <div className="agent-meta">
        <span>更新于 {dayjs(agent.updatedAt).format("MM-DD HH:mm")}</span>
      </div>
      <Space className="agent-actions" wrap>
        <Button type="primary" icon={<SendOutlined />} onClick={useAgent} disabled={agent.status !== "published"}>
          立即使用
        </Button>
        <Button onClick={() => navigate(`/agents/${agent.id}/detail`)}>详情</Button>
        {mine && (
          <>
            <Button icon={<EditOutlined />} onClick={() => navigate(`/agents/${agent.id}/edit`)}>编辑</Button>
            <Button icon={<LineChartOutlined />} onClick={() => navigate(`/agents/${agent.id}/evolution`)}>优化</Button>
            <Button
              icon={<CopyOutlined />}
              onClick={async () => {
                const copied = await api.copy(agent.id);
                message.success("已创建副本");
                navigate(`/agents/${copied.id}/edit`);
              }}
            />
            <Popconfirm
              title="删除这个 Agent？"
              description="删除后无法恢复，已发布版本也会停止使用。"
              onConfirm={async () => {
                await api.remove(agent.id);
                message.success("已删除");
                onChanged();
              }}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </>
        )}
      </Space>
    </Card>
  );
}

export function AgentCenter() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<"market" | "mine">("market");
  const [query, setQuery] = useState("");
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const { message } = App.useApp();

  useEffect(() => {
    const pending = consumePendingAgentActivation(sessionStorage);
    if (!pending) return;
    window.history.replaceState(null, "", "/agents");
    void (async () => {
      try {
        const knowledge = pending.requiresKnowledgeAuthorization
          && !hasRecentKnowledgeAuthorization(sessionStorage);
        const connector = pending.requiresFeishuConnectorAuthorization
          && !hasRecentFeishuConnectorAuthorization(sessionStorage);
        const connectorWrite = pending.requiresFeishuWriteAuthorization
          && !hasRecentFeishuWriteAuthorization(sessionStorage);
        const office = pending.requiresFeishuOfficeAuthorization
          && !hasRecentFeishuOfficeAuthorization(sessionStorage);
        const officeWrite = pending.requiresFeishuOfficeWriteAuthorization
          && !hasRecentFeishuOfficeWriteAuthorization(sessionStorage);
        if (knowledge || connector || connectorWrite || office || officeWrite) {
          message.loading({ content: "正在确认飞书访问权限…", key: "activate-agent" });
          await api.authorizeFeishuAccess({ knowledge, connector, connectorWrite, office, officeWrite });
        }
        await api.activate(pending.id);
        message.success({
          content: `已切换到“${pending.name}”，现在可以返回飞书 Bot 对话`,
          key: "activate-agent"
        });
      } catch (error) {
        message.error({
          content: (error as Error).message || "Agent 启用失败，请重试",
          key: "activate-agent"
        });
      }
    })();
  }, [message]);

  useEffect(() => {
    const pending = consumePendingFeishuIdentitySync(sessionStorage);
    if (!pending) return;
    window.history.replaceState(null, "", "/agents");
    void (async () => {
      try {
        message.loading({ content: "正在同步飞书员工身份…", key: "sync-feishu-identity" });
        await api.refreshFeishuIdentity();
        message.success({ content: "当前员工身份映射已更新", key: "sync-feishu-identity" });
        navigate(pending.returnPath, { replace: true });
      } catch (error) {
        message.error({
          content: (error as Error).message || "飞书员工身份同步失败，请重试",
          key: "sync-feishu-identity"
        });
      }
    })();
  }, [message, navigate]);

  useEffect(() => {
    const pending = consumePendingFeishuOfficeConnection(sessionStorage);
    if (!pending) return;
    window.history.replaceState(null, "", "/agents");
    void (async () => {
      try {
        message.loading({ content: "正在进行飞书员工授权…", key: "connect-feishu-office" });
        await api.reauthorizeFeishuOfficeAccess();
        await api.connectConnector(pending.id);
        message.success({
          content: `${pending.name}已连接`,
          key: "connect-feishu-office"
        });
        navigate("/capabilities?kind=connector", { replace: true });
      } catch (error) {
        message.error({
          content: (error as Error).message || "飞书办公连接器授权失败，请重试",
          key: "connect-feishu-office"
        });
      }
    })();
  }, [message, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      setAgents(await (scope === "market" ? api.market(query) : api.mine(query)));
    } catch (error) {
      message.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer);
  }, [scope, query]);

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Agent 中心</Typography.Title>
          <Typography.Text type="secondary">发现团队的 AI 员工，或创建属于自己的 Agent</Typography.Text>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate("/agents/create")}
        >
          创建 Agent
        </Button>
      </div>
      <div className="toolbar">
        <Segmented
          value={scope}
          onChange={(value) => setScope(value as "market" | "mine")}
          options={[
            { label: "公司 Agent", value: "market" },
            { label: "我的 Agent", value: "mine" }
          ]}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="搜索名称、描述、创建人或标签"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="search-input"
        />
      </div>
      {loading ? (
        <div className="empty-state">
          <Spin tip={scope === "mine" ? "正在加载我的 Agent…" : "正在加载公司 Agent…"}>
            <div style={{ height: 72 }} />
          </Spin>
        </div>
      ) : agents.length ? (
          <Row gutter={[16, 16]}>
            {agents.map((agent) => (
              <Col xs={24} md={12} xl={8} key={agent.id}>
                <AgentCard agent={agent} mine={scope === "mine"} onChanged={load} />
              </Col>
            ))}
          </Row>
        ) : (
          <Empty
            className="empty-state"
            description={scope === "mine" ? "你还没有创建 Agent" : "暂时没有公司 Agent"}
          >
            {scope === "mine" && (
              <Button type="primary" onClick={() => navigate("/agents/create")}>创建第一个 Agent</Button>
            )}
          </Empty>
        )}
    </div>
  );
}
